import L from 'leaflet';
import * as R from 'ramda';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature geo-fencing-location
import { locationSettings } from './settings/location-settings';
//////////////////////////////////////////////////

const IDLE_MODE = 'IDLE';
const EDIT_MODE = 'EDIT';
const VIEW_MODE = 'VIEW';

const getFilterProps = () => R.mapObjIndexed(
  ({ name, options, collection, placeholder, filterType = 'string' }, value) => {
    let optionsToUse = options;

    if (G.isFunction(options)) optionsToUse = options();

    return ({
      value,
      collection,
      options: optionsToUse,
      [GC.FIELD_TYPE]: filterType,
      placeholder: G.ifElse(
        R.and(R.equals(filterType, 'selectMultiple'), R.isNil(placeholder)),
        { text: 'Choose a status', key: 'titles:choose-a-status' },
        placeholder,
      ),
      [GC.FIELD_NAME]: G.ifElse(
        G.isArray(name),
        // eslint-disable-next-line max-len
        `${G.getWindowLocale(name[0])}: ${G.getWindowLocale(name[1])}${G.ifElse(R.gt(R.length(name), 2), `: ${G.getWindowLocale(name[2])}`, '')}`,
        G.getWindowLocale(name, '...'),
      ),
    });
  },
  locationSettings,
);

const prepareLocationForSubmitting = (data) => R.compose(
  R.assoc(GC.FIELD_ADDRESS, R.prop(GC.FIELD_ADDRESS_1, data)),
  R.assoc(GC.FIELD_FLEET_LOCATION_RADIUS_UOM, R.prop(GC.FIELD_RADIUS, data) ? G.getFootOrMeterUomByUomSystem() : null),
  R.pick([
    GC.FIELD_ZIP,
    GC.FIELD_CITY,
    GC.FIELD_NAME,
    GC.FIELD_TYPE,
    GC.FIELD_STATE,
    GC.FIELD_COORDS,
    GC.FIELD_COUNTRY,
    GC.FIELD_LATITUDE,
    GC.FIELD_LONGITUDE,
    GC.FIELD_SHAPE_TYPE,
    GC.FIELD_FLEET_LOCATION_RADIUS,
  ]),
)(data);

const remapLocationValues = (data) => R.mergeLeft({
  [GC.FIELD_ADDRESS_1]: R.propOr('', GC.FIELD_ADDRESS, data),
  [GC.FIELD_STATE]: G.getStateValueByConfig(R.prop(GC.FIELD_STATE, data)),
  [GC.FIELD_TYPE]: R.pathOr(null, [GC.FIELD_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], data),
}, data);

const getDistanceUomLocale = () => {
  if (G.isMetricUomSystem()) return G.getWindowLocale('titles:meters', 'Meters');

  return G.getWindowLocale('titles:feet', 'Feet');
};

const coefficient = 3.28084;

const metersToFeet = (meters) => G.toFixed(meters * coefficient, 0);

const feetToMeters = (feet) => G.toFixed(feet / coefficient, 0);

const degCoefficient = 110574;

const differenceByRadius = (radius) => G.ifElse(G.isNotNil(radius), radius / degCoefficient, 0.1);

const convertLocationWithRadius = (location) => {
  const radius = R.prop(GC.FIELD_FLEET_LOCATION_RADIUS, location);
  const radiusUom = R.prop(GC.FIELD_FLEET_LOCATION_RADIUS_UOM, location);

  const isMetric = G.isMetricUomSystem();
  const currentUom = G.ifElse(isMetric, GC.UOM_METER, GC.UOM_FOOT);

  if (R.equals(currentUom, radiusUom)) return location;

  const currentLocation = R.assoc([GC.FIELD_FLEET_LOCATION_RADIUS_UOM], currentUom, location);

  if (isMetric) {
    return R.assoc([GC.FIELD_FLEET_LOCATION_RADIUS], feetToMeters(radius), currentLocation);
  }

  return R.assoc([GC.FIELD_FLEET_LOCATION_RADIUS], metersToFeet(radius), currentLocation);
};

const isLayerTypeCircle = (layerType) => R.equals('circle', layerType);

const toLatLngPositions = (coords) => R.map(({ latitude, longitude }) => [latitude, longitude], coords);

const DEFAULT_CENTER = [38.755157, -98.269035];

const getMapCenterAndBounds = (shapeType, position = {}, currentRadius, coords) => {
  if (R.equals(shapeType, GC.SHAPE_TYPE_POLYGON)) {
    if (G.isNilOrEmpty(coords)) return { center: DEFAULT_CENTER };

    const positions = toLatLngPositions(coords);
    const bounds = L.latLngBounds(positions);

    return { bounds, center: bounds.getCenter() };
  }

  if (G.isNilOrEmpty(position)) return { center: DEFAULT_CENTER };

  const latitude = R.prop(GC.FIELD_LATITUDE, position);
  const longitude = R.prop(GC.FIELD_LONGITUDE, position);

  const diff = differenceByRadius(currentRadius);
  const southWest = L.latLng(latitude + diff, longitude + diff);
  const northEast = L.latLng(latitude - diff, longitude - diff);
  const bounds = L.latLngBounds(southWest, northEast);

  return { bounds, center: [latitude, longitude] };
};

export {
  IDLE_MODE,
  EDIT_MODE,
  VIEW_MODE,
  metersToFeet,
  feetToMeters,
  getFilterProps,
  toLatLngPositions,
  isLayerTypeCircle,
  differenceByRadius,
  remapLocationValues,
  getDistanceUomLocale,
  getMapCenterAndBounds,
  convertLocationWithRadius,
  prepareLocationForSubmitting,
};
